<template>
    <div
        class="grid grid-cols-12 gap-4 lg:gap-6"
        :class="{
            'xl:grid-rows-2': showGraphs,
        }"
    >
        <div
            class="col-span-12"
            :class="{
                'lg:col-span-6 xl:col-span-5 xl:row-span-2': showGraphs,
            }"
        >
            <vx-box-glass class="text-slate-700 font-sans">
                <vx-wallpaper :icon="props.templateView ? faGrid2Plus : faCube"></vx-wallpaper>
                <div class="relative p-6 lg:pl-10 lg:py-10 lg:pr-6 xl:p-10">
                    <h2 class="mb-2 text-xl font-bold">
                        Overview
                        <font-awesome-icon widthAuto :icon="faTelescope"></font-awesome-icon>
                    </h2>
                    <p class="text-sm mb-3 font-sans text-slate-700 lg:text-base">
                        <template v-if="props.templateView">
                            Segments that can be shared with Groups. Individual Groups will be able to use <br />
                            these Segment Templates in their Quick Blasts.
                        </template>
                        <template v-else>
                            A segment is a set of contacts grouped together by filters. Auto-refreshing segments refresh
                            automatically while static segments need to be refreshed manually. Each segment has an
                            audience type that limits the sort of messages that can be sent.
                        </template>
                    </p>
                    <a
                        :href="docsLink"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-sm font-sans font-normal text-sky-500 underline lg:text-base"
                    >
                        See Docs for More
                        <font-awesome-icon widthAuto class="ml-2" :icon="faArrowRightLong"></font-awesome-icon>
                    </a>
                </div>
            </vx-box-glass>
        </div>
        <div v-if="showGraphs" class="col-span-12 lg:col-span-6 xl:col-span-7">
            <vx-chart-categorical
                :loading="audienceTypeLoading"
                :data="audienceTypeCount"
                :colors="audienceTypeColors"
                :axis="{
                    x: {
                        key: 'count',
                    },
                }"
            >
                <template v-slot:title> Audience Type </template>
            </vx-chart-categorical>
        </div>
        <div v-if="showGraphs" class="col-span-12 xl:col-span-7">
            <vx-chart-categorical
                :loading="refreshTypeLoading"
                :data="refreshTypeCount"
                :colors="refreshTypeColors"
                :axis="{
                    x: {
                        key: 'count',
                    },
                }"
            >
                <template v-slot:title> Refresh Type </template>
            </vx-chart-categorical>
        </div>
    </div>
</template>

<script setup>
import { VxBoxGlass, VxChartCategorical, VxWallpaper } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowRightLong, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { faTelescope, faCube } from '@fortawesome/pro-regular-svg-icons';
import { ref, computed } from 'vue';
import { segmentsClient } from '~/services';
import { generateLegendColors } from '../../../utils/analytics';
import { uniqBy } from '~/utils/helpers';
import { SegmentType, SegmentTypeToLabel } from '../../../constants/segment';
import { audienceTypeLabel } from '../../../constants/campaign';

const props = defineProps({
    orgUnitId: Number,
    groupId: String,
    templateView: {
        type: Boolean,
        default: false,
    },
});

const teamId = Spark.state.currentTeam.id;

const showGraphs = computed(() => !props.orgUnitId && !props.groupId && !props.templateView);

const audienceTypeLoading = ref(true);
const audienceTypeCount = ref([]);
const audienceTypeColors = ref([]);

const refreshTypeLoading = ref(true);
const refreshTypeCount = ref([]);
const refreshTypeColors = ref([]);

if (showGraphs.value) {
    segmentsClient
        .countByAudienceType({ teamId })
        .then((res) => {
            audienceTypeCount.value = res.data.data.map((item) => ({
                label: audienceTypeLabel(item.audience_type),
                count: item.count,
            }));

            audienceTypeColors.value = generateLegendColors(
                uniqBy(audienceTypeCount.value, 'label').map((item) => item.label)
            );
        })
        .finally(() => (audienceTypeLoading.value = false));

    segmentsClient
        .countByRefreshType({ teamId })
        .then((res) => {
            refreshTypeCount.value = res.data.data.map((item) => ({
                label: SegmentTypeToLabel[item.refresh_type],
                count: item.count,
            }));

            refreshTypeColors.value = [
                {
                    // violet
                    label: SegmentTypeToLabel[SegmentType.AUTO_REFRESH],
                    iconColor: '#8B5CF6',
                    bgColor: '#C4B5FD',
                },
                {
                    // green
                    label: SegmentTypeToLabel[SegmentType.STATIC],
                    iconColor: '#22c55e',
                    bgColor: '#86efac',
                },
            ];
        })
        .finally(() => (refreshTypeLoading.value = false));
}

const docsLink = props.templateView
    ? 'https://explore.voxie.com/voxiesupport/hc/en-us/articles/segment-templates'
    : 'https://explore.voxie.com/voxiesupport/hc/en-us/articles/segments';
</script>
